import { cs } from '../../lib/currency';
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Download,
  Shirt,
  AlertCircle,
  CheckCircle,
  Edit,
  Search,
  Users,
  Ruler,
  FileSpreadsheet,
  Plus,
  Megaphone,
  Package,
  Eye,
  Loader2,
  Trash2,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { Navigate } from 'react-router-dom';
import { useFeatures } from '../../context/FeatureContext';
import AdminAddUniformOrderDialog from '../../components/admin/AdminAddUniformOrderDialog';
import UniformOrdersTable from '../../components/admin/UniformOrdersTable';

// iter337 — Give auto-generated campaign items a stable, human slug id
// derived from their label (e.g. "Full Kit" -> "full_kit") so orders
// placed against standard items (full_kit/shirt/cap/pants) keep matching
// on recompute. Existing meaningful ids are preserved.
const slugifyItemId = (label) =>
  (label || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const normalizeCampaignItems = (items) => {
  const used = new Set();
  return (items || []).map((it, idx) => {
    let id = it.id;
    if (!id || /^item_\d+$/.test(id)) {
      id = slugifyItemId(it.label) || `item_${idx + 1}`;
    }
    let unique = id;
    let n = 2;
    while (used.has(unique)) { unique = `${id}_${n++}`; }
    used.add(unique);
    return { ...it, id: unique };
  });
};


const UniformOrder = () => {
  const { isPlanFeatureEnabled } = useFeatures();
  const [activeTab, setActiveTab] = useState('campaigns');
  const [players, setPlayers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMissing, setFilterMissing] = useState(false);
  
  // Campaign state
  const [campaigns, setCampaigns] = useState([]);
  const [orders, setOrders] = useState([]);
  const [recomputing, setRecomputing] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignSummary, setCampaignSummary] = useState(null);
  
  // Create campaign dialog
  const [createCampaignDialog, setCreateCampaignDialog] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    title: '',
    description: '',
    deadline: '',
    items: [],
    visible_to_roles: ['parent']  // Default visibility
  });
  const [savingCampaign, setSavingCampaign] = useState(false);
  
  // Edit campaign dialog
  const [editCampaignDialog, setEditCampaignDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  
  // Order detail dialog
  const [orderDetailDialog, setOrderDetailDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Edit order dialog
  const [editOrderDialog, setEditOrderDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editOrderForm, setEditOrderForm] = useState({
    items: [],
    notes: '',
    status: 'pending'
  });
  
  // Delete order confirmation
  const [deleteOrderDialog, setDeleteOrderDialog] = useState(false);
  // iter307aj — Admin "Add Order" dialog (walk-in / phone orders).
  const [addOrderDialog, setAddOrderDialog] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  
  // Edit dialog
  const [editDialog, setEditDialog] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [measurements, setMeasurements] = useState({
    shirt_name: '',
    shirt_number: '',
    chest_size: '',
    shirt_length: '',
    sleeves: '',
    waist_size: '',
    trouser_length: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Sorting state for measurements table
  const [sortConfig, setSortConfig] = useState({ key: 'full_name', direction: 'asc' });
  const [exportingMeasurements, setExportingMeasurements] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedCampaign) {
      fetchCampaignOrders(selectedCampaign.id);
    }
  }, [selectedCampaign]);

  const fetchData = async () => {
    try {
      const [playersRes, summaryRes, campaignsRes] = await Promise.all([
        api.get('/players'),
        api.get('/admin/uniform-order/summary'),
        api.get('/uniform/campaigns')
      ]);
      setPlayers(playersRes.data);
      setSummary(summaryRes.data);
      setCampaigns(campaignsRes.data || []);
      
      // Auto-select first active campaign
      const activeCampaign = campaignsRes.data?.find(c => c.is_active);
      if (activeCampaign) {
        setSelectedCampaign(activeCampaign);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaignOrders = async (campaignId) => {
    try {
      const [ordersRes, summaryRes] = await Promise.all([
        api.get(`/uniform/orders?campaign_id=${campaignId}`),
        api.get(`/uniform/campaigns/${campaignId}/summary`)
      ]);
      setOrders(ordersRes.data || []);
      setCampaignSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to fetch campaign orders');
    }
  };

  // iter337 — apply the campaign's current per-item pricing to every
  // (un-invoiced) order so old $0/blank orders pick up prices.
  const recomputeTotals = async () => {
    if (!selectedCampaign) return;
    setRecomputing(true);
    try {
      const { data } = await api.post(`/uniform/campaigns/${selectedCampaign.id}/recompute-order-totals`);
      toast.success(data.message || 'Pricing updated');
      await fetchCampaignOrders(selectedCampaign.id);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setRecomputing(false);
    }
  };


  const createCampaign = async () => {
    if (!campaignForm.title.trim()) {
      toast.error('Please enter a campaign title');
      return;
    }
    
    if (campaignForm.items.length === 0) {
      toast.error('Please add at least one uniform item');
      return;
    }
    
    setSavingCampaign(true);
    try {
      await api.post('/uniform/campaigns', { ...campaignForm, items: normalizeCampaignItems(campaignForm.items) });
      toast.success('Uniform campaign created! Visible to selected roles.');
      setCreateCampaignDialog(false);
      setCampaignForm({ title: '', description: '', deadline: '', items: [], visible_to_roles: ['parent'] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setSavingCampaign(false);
    }
  };

  const addItemToCampaign = () => {
    const newItem = {
      id: `item_${Date.now()}`,
      label: '',
      price: 0,
      price_min: 0,
      price_max: 0,
      requires_shirt_sizing: false,
      requires_pants_sizing: false,
      has_sleeve_option: false
    };
    setCampaignForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const updateCampaignItem = (index, field, value) => {
    setCampaignForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeCampaignItem = (index) => {
    setCampaignForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const toggleRoleVisibility = (role) => {
    setCampaignForm(prev => {
      const currentRoles = prev.visible_to_roles || [];
      if (currentRoles.includes(role)) {
        return { ...prev, visible_to_roles: currentRoles.filter(r => r !== role) };
      } else {
        return { ...prev, visible_to_roles: [...currentRoles, role] };
      }
    });
  };

  const openEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignForm({
      title: campaign.title || '',
      description: campaign.description || '',
      deadline: campaign.deadline || '',
      items: campaign.items || [],
      visible_to_roles: campaign.visible_to_roles || ['parent']
    });
    setEditCampaignDialog(true);
  };

  const updateCampaign = async () => {
    if (!campaignForm.title.trim()) {
      toast.error('Please enter a campaign title');
      return;
    }
    
    setSavingCampaign(true);
    try {
      await api.put(`/uniform/campaigns/${editingCampaign.id}`, { ...campaignForm, items: normalizeCampaignItems(campaignForm.items) });
      toast.success('Campaign updated successfully');
      setEditCampaignDialog(false);
      setEditingCampaign(null);
      setCampaignForm({ title: '', description: '', deadline: '', items: [], visible_to_roles: ['parent'] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update campaign');
    } finally {
      setSavingCampaign(false);
    }
  };

  const toggleCampaignActive = async (campaign) => {
    try {
      await api.put(`/uniform/campaigns/${campaign.id}`, {
        is_active: !campaign.is_active
      });
      toast.success(campaign.is_active ? 'Campaign deactivated' : 'Campaign activated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update campaign');
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const res = await api.put(`/uniform/orders/${orderId}/status?status=${status}`);
      const data = res?.data || {};
      // iter335 — the backend returns 200 with invoice_error/no_invoice
      // in edge cases; reflect that instead of implying a bill was raised.
      if (data.invoice_error) {
        toast.warning(`Order marked ${status}, but the invoice could not be created: ${data.invoice_error}`);
      } else if (data.no_invoice) {
        toast.warning(data.message || `Order marked ${status}. No invoice was generated.`);
      } else if (status === 'processed' && data.invoice) {
        toast.success(`Order processed — invoice for ${cs()}${Number(data.invoice.amount || 0).toFixed(2)} created`);
      } else {
        toast.success(`Order marked as ${status}`);
      }
      if (selectedCampaign) {
        fetchCampaignOrders(selectedCampaign.id);
      }
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  // Helper to get item label from campaign items
  const getItemLabel = (itemTypeId) => {
    if (!selectedCampaign?.items) return itemTypeId?.replace('_', ' ') || 'Unknown';
    const item = selectedCampaign.items.find(i => i.id === itemTypeId);
    return item?.label || itemTypeId?.replace('_', ' ') || 'Unknown';
  };

  const openEditOrder = (order) => {
    setEditingOrder(order);
    setEditOrderForm({
      items: order.items || [],
      notes: order.notes || '',
      status: order.status || 'pending'
    });
    setEditOrderDialog(true);
  };

  const updateOrder = async () => {
    if (!editingOrder) return;
    
    try {
      await api.put(`/uniform/orders/${editingOrder.id}`, editOrderForm);
      toast.success('Order updated successfully');
      setEditOrderDialog(false);
      setEditingOrder(null);
      if (selectedCampaign) {
        fetchCampaignOrders(selectedCampaign.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    }
  };

  const confirmDeleteOrder = (order) => {
    setOrderToDelete(order);
    setDeleteOrderDialog(true);
  };

  const deleteOrder = async () => {
    if (!orderToDelete) return;
    
    try {
      await api.delete(`/uniform/orders/${orderToDelete.id}`);
      toast.success('Order deleted successfully');
      setDeleteOrderDialog(false);
      setOrderToDelete(null);
      if (selectedCampaign) {
        fetchCampaignOrders(selectedCampaign.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };

  const addItemToOrder = () => {
    const newItem = {
      item_type: 'shirt',
      quantity: 1,
      sleeves: 'short'
    };
    setEditOrderForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const updateOrderItem = (index, field, value) => {
    setEditOrderForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeOrderItem = (index) => {
    setEditOrderForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/admin/uniform-order/export', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'uniform_order.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Uniform order CSV downloaded');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const openEditDialog = (player) => {
    setSelectedPlayer(player);
    setMeasurements({
      shirt_name: player.shirt_name || '',
      shirt_number: player.shirt_number || '',
      chest_size: player.chest_size || '',
      shirt_length: player.shirt_length || '',
      sleeves: player.sleeves || '',
      waist_size: player.waist_size || '',
      trouser_length: player.trouser_length || ''
    });
    setEditDialog(true);
  };

  const handleSaveMeasurements = async () => {
    if (!selectedPlayer) return;
    
    setSaving(true);
    try {
      await api.put(`/players/${selectedPlayer.id}`, {
        shirt_name: measurements.shirt_name || null,
        shirt_number: measurements.shirt_number ? parseInt(measurements.shirt_number) : null,
        chest_size: measurements.chest_size ? parseInt(measurements.chest_size) : null,
        shirt_length: measurements.shirt_length ? parseFloat(measurements.shirt_length) : null,
        sleeves: measurements.sleeves || null,
        waist_size: measurements.waist_size ? parseInt(measurements.waist_size) : null,
        trouser_length: measurements.trouser_length ? parseFloat(measurements.trouser_length) : null
      });
      
      toast.success('Measurements saved');
      setEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save measurements');
    } finally {
      setSaving(false);
    }
  };

  const hasMeasurements = (player) => {
    return player.chest_size && player.waist_size;
  };
  
  // Sorting function for measurements table
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const filteredPlayers = players.filter(player => {
    const matchesSearch = !searchTerm || 
      (player.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (player.parent_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = !filterMissing || !hasMeasurements(player);
    
    return matchesSearch && matchesFilter;
  });
  
  // Sort the filtered players
  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    const key = sortConfig.key;
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    
    let aVal = a[key];
    let bVal = b[key];
    
    // Handle nulls - put them at the end
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;
    
    // Numeric comparison for measurement fields
    if (['shirt_number', 'chest_size', 'shirt_length', 'waist_size', 'trouser_length'].includes(key)) {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
      return (aVal - bVal) * direction;
    }
    
    // String comparison
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.toLowerCase().localeCompare(bVal.toLowerCase()) * direction;
    }
    
    return 0;
  });
  
  // Export measurements function
  const exportMeasurements = async () => {
    setExportingMeasurements(true);
    try {
      const response = await api.get('/uniform/measurements/export', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'player_measurements.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Measurements exported successfully');
    } catch (error) {
      toast.error('Failed to export measurements');
    } finally {
      setExportingMeasurements(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Uniform Order">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  // iter335 — Route-level entitlement guard. navConfig hides the link
  // for non-entitled tenants, but a direct URL would still render this
  // page; redirect Starter/lane_only admins away (backend also 402s the
  // write endpoints). `isPlanFeatureEnabled` returns true while the plan
  // list is still loading, so this only fires after a confirmed miss.
  if (!isPlanFeatureEnabled('commerce.uniforms')) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <DashboardLayout title="Uniform Order">
      <div className="space-y-6" data-testid="uniform-order-page">
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">Manage uniform campaigns, orders, and player measurements</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="campaigns" className="flex items-center gap-2">
              <Megaphone className="h-4 w-4" />
              Campaigns & Orders
            </TabsTrigger>
            <TabsTrigger value="measurements" className="flex items-center gap-2">
              <Ruler className="h-4 w-4" />
              Player Measurements
            </TabsTrigger>
          </TabsList>

          {/* Campaigns Tab */}
          <TabsContent value="campaigns" className="space-y-6">
            {/* Create Campaign */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Megaphone className="h-5 w-5" />
                      Uniform Campaigns
                    </CardTitle>
                    <CardDescription>
                      Create campaigns to notify parents to verify and order uniforms
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => setCreateCampaignDialog(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="create-campaign-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Campaign
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {campaigns.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Megaphone className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>No campaigns yet. Create one to notify parents.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {campaigns.map(campaign => (
                      <div
                        key={campaign.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedCampaign?.id === campaign.id 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'hover:bg-slate-50'
                        }`}
                        onClick={() => setSelectedCampaign(campaign)}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{campaign.title}</h4>
                              {campaign.is_active ? (
                                <Badge className="bg-green-100 text-green-700">Active</Badge>
                              ) : (
                                <Badge className="bg-slate-100 text-slate-600">Inactive</Badge>
                              )}
                            </div>
                            {campaign.description && (
                              <p className="text-sm text-slate-500 mt-1">{campaign.description}</p>
                            )}
                            {campaign.items && campaign.items.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {campaign.items.map((item, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {item.label}: {cs()}{item.price != null && item.price > 0
                                      ? item.price
                                      : (item.price_min === item.price_max
                                        ? item.price_min
                                        : `${item.price_min}-${item.price_max}`)}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            <p className="text-xs text-slate-400 mt-2">
                              Created {new Date(campaign.created_at).toLocaleDateString()}
                              {campaign.deadline && ` • Deadline: ${new Date(campaign.deadline).toLocaleDateString()}`}
                            </p>
                            {campaign.visible_to_roles && campaign.visible_to_roles.length > 0 && (
                              <div className="flex items-center gap-1 mt-2">
                                <span className="text-xs text-slate-500">Visible to:</span>
                                {campaign.visible_to_roles.map(role => (
                                  <Badge key={role} variant="outline" className="text-xs capitalize">
                                    {role}s
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditCampaign(campaign);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCampaignActive(campaign);
                              }}
                            >
                              {campaign.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Campaign Orders */}
            {selectedCampaign && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Package className="h-5 w-5" />
                        Orders for "{selectedCampaign.title}"
                      </CardTitle>
                      {campaignSummary && (
                        <CardDescription className="mt-2 flex items-center gap-4">
                          <span>Total: <strong>{campaignSummary.total_orders}</strong></span>
                          <span className="text-amber-600">Pending: <strong>{campaignSummary.status_counts?.pending || 0}</strong></span>
                          <span className="text-green-600">Processed: <strong>{campaignSummary.status_counts?.processed || 0}</strong></span>
                        </CardDescription>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {/* iter307aj — Admin can create orders on behalf
                          of any parent. Marking "Processed" later
                          auto-generates a manual invoice in the "Other"
                          category. Always shown (also when no orders
                          exist yet). */}
                      <Button
                        onClick={() => setAddOrderDialog(true)}
                        data-testid="admin-add-order-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Order
                      </Button>
                      {orders.length > 0 && (
                        <Button
                          variant="outline"
                          onClick={recomputeTotals}
                          disabled={recomputing}
                          data-testid="update-pricing-btn"
                          title="Recalculate every order's total from the campaign's current item prices"
                        >
                          <RefreshCw className={`h-4 w-4 mr-2 ${recomputing ? 'animate-spin' : ''}`} />
                          {recomputing ? 'Updating…' : 'Update Pricing'}
                        </Button>
                      )}
                      {orders.length > 0 && (
                        <>
                          <Button
                            variant="outline"
                            onClick={async () => {
                            try {
                              const response = await api.get(`/uniform/campaigns/${selectedCampaign.id}/export`, {
                                responseType: 'blob'
                              });
                              const blob = new Blob([response.data], { type: 'text/csv' });
                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = `uniform_orders_${selectedCampaign.title.replace(/\s+/g, '_')}.csv`;
                              link.click();
                              window.URL.revokeObjectURL(url);
                              toast.success('Orders exported to CSV');
                            } catch (error) {
                              toast.error('Export failed');
                            }
                          }}
                          data-testid="export-orders-csv"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Export Orders
                        </Button>
                        <Button
                          variant="outline"
                          onClick={async () => {
                            try {
                              const response = await api.get(`/uniform/campaigns/${selectedCampaign.id}/export-verifications`, {
                                responseType: 'blob'
                              });
                              const blob = new Blob([response.data], { type: 'text/csv' });
                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = `uniform_verifications_${selectedCampaign.title.replace(/\s+/g, '_')}.csv`;
                              link.click();
                              window.URL.revokeObjectURL(url);
                              toast.success('Verifications exported to CSV');
                            } catch (error) {
                              toast.error('Export failed');
                            }
                          }}
                          data-testid="export-verifications-csv"
                        >
                          <FileSpreadsheet className="h-4 w-4 mr-2" />
                          Export Verifications (Full Kit)
                        </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {orders.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Package className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>No orders submitted for this campaign yet.</p>
                    </div>
                  ) : (
                    <UniformOrdersTable
                      orders={orders}
                      getItemLabel={getItemLabel}
                      onView={(o) => { setSelectedOrder(o); setOrderDetailDialog(true); }}
                      onEdit={(o) => openEditOrder(o)}
                      onDelete={(o) => confirmDeleteOrder(o)}
                      onMarkProcessed={(o) => updateOrderStatus(o.id, 'processed')}
                    />
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Measurements Tab */}
          <TabsContent value="measurements" className="space-y-6">
            <div className="space-y-3">
              <p className="text-slate-600">View and edit player uniform measurements</p>
              <div className="flex flex-wrap items-center gap-2">
              <Button 
                onClick={handleExport}
                className="bg-green-600 hover:bg-green-700"
                data-testid="export-uniform-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Export Uniform Order CSV
              </Button>
              </div>
            </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Players</p>
                    <p className="text-2xl font-bold">{summary.total_players}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">With Measurements</p>
                    <p className="text-2xl font-bold text-green-600">{summary.with_measurements}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Missing Measurements</p>
                    <p className="text-2xl font-bold text-amber-600">{summary.missing_measurements}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Ruler className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Completion Rate</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {summary.total_players > 0 
                        ? Math.round((summary.with_measurements / summary.total_players) * 100) 
                        : 0}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Size Distribution */}
        {summary && (summary.chest_sizes && Object.keys(summary.chest_sizes).length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shirt className="h-4 w-4" />
                  Chest Size Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(summary.chest_sizes)
                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                    .map(([size, count]) => (
                      <Badge key={size} variant="outline" className="text-sm">
                        {size}" <span className="ml-1 text-blue-600 font-bold">×{count}</span>
                      </Badge>
                    ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4" />
                  Waist Size Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(summary.waist_sizes)
                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                    .map(([size, count]) => (
                      <Badge key={size} variant="outline" className="text-sm">
                        {size}" <span className="ml-1 text-green-600 font-bold">×{count}</span>
                      </Badge>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Players Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Shirt className="h-5 w-5" />
                Player Measurements
              </CardTitle>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search players..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-64"
                    data-testid="search-players-input"
                  />
                </div>
                <Button
                  variant={filterMissing ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterMissing(!filterMissing)}
                  data-testid="filter-missing-btn"
                >
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {filterMissing ? 'Show All' : 'Missing Only'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportMeasurements}
                  disabled={exportingMeasurements}
                  data-testid="export-measurements-btn"
                >
                  {exportingMeasurements ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-1" />
                  )}
                  Export CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('full_name')}
                  >
                    Player {getSortIcon('full_name')}
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('shirt_name')}
                  >
                    Shirt Name {getSortIcon('shirt_name')}
                  </TableHead>
                  <TableHead 
                    className="text-center cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('shirt_number')}
                  >
                    Number {getSortIcon('shirt_number')}
                  </TableHead>
                  <TableHead 
                    className="text-center cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('chest_size')}
                  >
                    Chest {getSortIcon('chest_size')}
                  </TableHead>
                  <TableHead 
                    className="text-center cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('shirt_length')}
                  >
                    Shirt Len {getSortIcon('shirt_length')}
                  </TableHead>
                  <TableHead 
                    className="text-center cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('sleeves')}
                  >
                    Sleeves {getSortIcon('sleeves')}
                  </TableHead>
                  <TableHead 
                    className="text-center cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('waist_size')}
                  >
                    Waist {getSortIcon('waist_size')}
                  </TableHead>
                  <TableHead 
                    className="text-center cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('trouser_length')}
                  >
                    Trouser Len {getSortIcon('trouser_length')}
                  </TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedPlayers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                      {searchTerm || filterMissing ? 'No players match your filters' : 'No players found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedPlayers.map(player => (
                    <TableRow key={player.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{player.full_name}</p>
                          <p className="text-xs text-slate-500">
                            {player.age ? `${player.age} yrs` : ''} 
                            {player.gender && ` • ${player.gender}`}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {player.shirt_name ? (
                          <span className="font-medium text-blue-600">{player.shirt_name}</span>
                        ) : (
                          <span className="text-slate-400 text-sm">Not set</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {player.shirt_number !== null && player.shirt_number !== undefined ? (
                          <Badge className="bg-blue-100 text-blue-700 font-bold">{player.shirt_number}</Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {player.chest_size ? (
                          <Badge variant="outline">{player.chest_size}"</Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {player.shirt_length ? (
                          <Badge variant="outline">{player.shirt_length}"</Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {player.sleeves ? (
                          <Badge variant="outline">{player.sleeves}</Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {player.waist_size ? (
                          <Badge variant="outline">{player.waist_size}"</Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {player.trouser_length ? (
                          <Badge variant="outline">{player.trouser_length}"</Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {hasMeasurements(player) ? (
                          <Badge className="bg-green-100 text-green-700">Complete</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">Incomplete</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(player)}
                          data-testid={`edit-measurements-${player.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Measurements Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Ruler className="h-5 w-5 text-blue-500" />
                Edit Measurements - {selectedPlayer?.full_name}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Shirt Personalization */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-sm font-medium text-blue-700 mb-3">Shirt Personalization</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name on Shirt</Label>
                    <Input
                      type="text"
                      maxLength="20"
                      placeholder="e.g., SMITH"
                      value={measurements.shirt_name}
                      onChange={(e) => setMeasurements(prev => ({ ...prev, shirt_name: e.target.value.toUpperCase() }))}
                      data-testid="shirt-name-input"
                      className="uppercase"
                    />
                    <p className="text-xs text-slate-500">Max 20 characters</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Jersey Number</Label>
                    <Input
                      type="number"
                      min="0"
                      max="99"
                      placeholder="e.g., 7"
                      value={measurements.shirt_number}
                      onChange={(e) => setMeasurements(prev => ({ ...prev, shirt_number: e.target.value }))}
                      data-testid="shirt-number-input"
                    />
                    <p className="text-xs text-slate-500">0-99</p>
                  </div>
                </div>
              </div>

              {/* Measurements */}
              <p className="text-sm text-slate-500">
                All measurements are in inches.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Chest Size (inches)</Label>
                  <Input
                    type="number"
                    min="20"
                    max="50"
                    placeholder="e.g., 32"
                    value={measurements.chest_size}
                    onChange={(e) => setMeasurements(prev => ({ ...prev, chest_size: e.target.value }))}
                    data-testid="chest-size-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Shirt Length (inches)</Label>
                  <Input
                    type="number"
                    min="15"
                    max="40"
                    placeholder="e.g., 24"
                    value={measurements.shirt_length}
                    onChange={(e) => setMeasurements(prev => ({ ...prev, shirt_length: e.target.value }))}
                    data-testid="shirt-length-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sleeves</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                    value={measurements.sleeves}
                    onChange={(e) => setMeasurements(prev => ({ ...prev, sleeves: e.target.value }))}
                    data-testid="sleeves-input"
                  >
                    <option value="">Select...</option>
                    <option value="short">Short</option>
                    <option value="full">Full</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Waist Size (inches)</Label>
                  <Input
                    type="number"
                    min="20"
                    max="50"
                    placeholder="e.g., 28"
                    value={measurements.waist_size}
                    onChange={(e) => setMeasurements(prev => ({ ...prev, waist_size: e.target.value }))}
                    data-testid="waist-size-input"
                  />
                </div>

                <div className="space-y-2 col-span-2">
                  <Label>Trouser Length (inches)</Label>
                  <Input
                    type="number"
                    min="20"
                    max="50"
                    placeholder="e.g., 36"
                    value={measurements.trouser_length}
                    onChange={(e) => setMeasurements(prev => ({ ...prev, trouser_length: e.target.value }))}
                    data-testid="trouser-length-input"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveMeasurements}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-measurements-btn"
              >
                {saving ? 'Saving...' : 'Save Measurements'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Campaign Dialog */}
        <Dialog open={createCampaignDialog} onOpenChange={setCreateCampaignDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-blue-600" />
                Create Uniform Campaign
              </DialogTitle>
              <DialogDescription>
                Parents will see a notification banner to verify and order uniforms.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Campaign Title *</Label>
                <Input
                  value={campaignForm.title}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., 2026 Season Uniform Order"
                  data-testid="campaign-title-input"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional message to parents..."
                  rows={3}
                />
              </div>

              <div>
                <Label>Deadline (Optional)</Label>
                <Input
                  type="date"
                  value={campaignForm.deadline}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, deadline: e.target.value }))}
                />
              </div>

              {/* Role Visibility */}
              <div className="border rounded-lg p-4 bg-slate-50">
                <Label className="text-base font-semibold mb-3 block">Visible to Roles *</Label>
                <p className="text-xs text-slate-500 mb-3">Select which user roles can see this campaign</p>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={campaignForm.visible_to_roles?.includes('parent')}
                      onCheckedChange={() => toggleRoleVisibility('parent')}
                      data-testid="role-parent-checkbox"
                    />
                    <span className="text-sm">Parents</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={campaignForm.visible_to_roles?.includes('coach')}
                      onCheckedChange={() => toggleRoleVisibility('coach')}
                      data-testid="role-coach-checkbox"
                    />
                    <span className="text-sm">Coaches</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={campaignForm.visible_to_roles?.includes('staff')}
                      onCheckedChange={() => toggleRoleVisibility('staff')}
                      data-testid="role-staff-checkbox"
                    />
                    <span className="text-sm">Staff</span>
                  </label>
                </div>
                {(!campaignForm.visible_to_roles || campaignForm.visible_to_roles.length === 0) && (
                  <p className="text-xs text-red-500 mt-2">Please select at least one role</p>
                )}
              </div>

              {/* Items Configuration */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Uniform Items *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addItemToCampaign}
                    data-testid="add-item-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Item
                  </Button>
                </div>

                {campaignForm.items.length === 0 ? (
                  <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed">
                    <Shirt className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No items added yet</p>
                    <p className="text-xs text-slate-400">Click "Add Item" to configure uniform options</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {campaignForm.items.map((item, index) => (
                      <div key={item.id} className="p-4 border rounded-lg bg-slate-50">
                        <div className="flex items-start justify-between mb-3">
                          <span className="text-sm font-medium text-slate-600">Item {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCampaignItem(index)}
                            className="text-red-500 h-6 px-2"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-3 mb-3">
                          <div className="col-span-3">
                            <Label className="text-xs">Item Name *</Label>
                            <Input
                              value={item.label}
                              onChange={(e) => updateCampaignItem(index, 'label', e.target.value)}
                              placeholder="e.g., Full Kit (Shirt + Pants + Cap)"
                              className="h-9"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Price ($) *</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.price ?? 0}
                              onChange={(e) => updateCampaignItem(index, 'price', parseFloat(e.target.value) || 0)}
                              className="h-9"
                              data-testid="uniform-item-price-input"
                            />
                            <p className="text-[10px] text-slate-400 mt-0.5">Used for invoicing</p>
                          </div>
                          <div>
                            <Label className="text-xs">Min Price ($)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.price_min}
                              onChange={(e) => updateCampaignItem(index, 'price_min', parseFloat(e.target.value) || 0)}
                              className="h-9"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Max Price ($)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.price_max}
                              onChange={(e) => updateCampaignItem(index, 'price_max', parseFloat(e.target.value) || 0)}
                              className="h-9"
                            />
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-4 text-sm">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={item.requires_shirt_sizing}
                              onCheckedChange={(checked) => updateCampaignItem(index, 'requires_shirt_sizing', checked)}
                            />
                            <span>Requires chest/shirt sizing</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={item.requires_pants_sizing}
                              onCheckedChange={(checked) => updateCampaignItem(index, 'requires_pants_sizing', checked)}
                            />
                            <span>Requires waist/pants sizing</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={item.has_sleeve_option}
                              onCheckedChange={(checked) => updateCampaignItem(index, 'has_sleeve_option', checked)}
                            />
                            <span>Has sleeve option</span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateCampaignDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={createCampaign}
                disabled={savingCampaign}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {savingCampaign ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Campaign'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Campaign Dialog */}
        <Dialog open={editCampaignDialog} onOpenChange={setEditCampaignDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-blue-600" />
                Edit Campaign
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Campaign Title *</Label>
                <Input
                  value={campaignForm.title}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., 2026 Season Uniform Order"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional message to parents..."
                  rows={3}
                />
              </div>

              <div>
                <Label>Deadline (Optional)</Label>
                <Input
                  type="date"
                  value={campaignForm.deadline}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, deadline: e.target.value }))}
                />
              </div>

              {/* Role Visibility */}
              <div className="border rounded-lg p-4 bg-slate-50">
                <Label className="text-base font-semibold mb-3 block">Visible to Roles *</Label>
                <p className="text-xs text-slate-500 mb-3">Select which user roles can see this campaign</p>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={campaignForm.visible_to_roles?.includes('parent')}
                      onCheckedChange={() => toggleRoleVisibility('parent')}
                    />
                    <span className="text-sm">Parents</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={campaignForm.visible_to_roles?.includes('coach')}
                      onCheckedChange={() => toggleRoleVisibility('coach')}
                    />
                    <span className="text-sm">Coaches</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={campaignForm.visible_to_roles?.includes('staff')}
                      onCheckedChange={() => toggleRoleVisibility('staff')}
                    />
                    <span className="text-sm">Staff</span>
                  </label>
                </div>
                {(!campaignForm.visible_to_roles || campaignForm.visible_to_roles.length === 0) && (
                  <p className="text-xs text-red-500 mt-2">Please select at least one role</p>
                )}
              </div>

              {/* Items Configuration */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Uniform Items</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addItemToCampaign}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Item
                  </Button>
                </div>

                {campaignForm.items.length === 0 ? (
                  <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed">
                    <Shirt className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No items configured</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {campaignForm.items.map((item, index) => (
                      <div key={item.id} className="p-4 border rounded-lg bg-slate-50">
                        <div className="flex items-start justify-between mb-3">
                          <span className="text-sm font-medium text-slate-600">Item {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCampaignItem(index)}
                            className="text-red-500 h-6 px-2"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-3 mb-3">
                          <div className="col-span-3">
                            <Label className="text-xs">Item Name *</Label>
                            <Input
                              value={item.label}
                              onChange={(e) => updateCampaignItem(index, 'label', e.target.value)}
                              placeholder="e.g., Full Kit (Shirt + Pants + Cap)"
                              className="h-9"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Price ($) *</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.price ?? 0}
                              onChange={(e) => updateCampaignItem(index, 'price', parseFloat(e.target.value) || 0)}
                              className="h-9"
                              data-testid="uniform-item-price-input"
                            />
                            <p className="text-[10px] text-slate-400 mt-0.5">Used for invoicing</p>
                          </div>
                          <div>
                            <Label className="text-xs">Min Price ($)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.price_min}
                              onChange={(e) => updateCampaignItem(index, 'price_min', parseFloat(e.target.value) || 0)}
                              className="h-9"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Max Price ($)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.price_max}
                              onChange={(e) => updateCampaignItem(index, 'price_max', parseFloat(e.target.value) || 0)}
                              className="h-9"
                            />
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-4 text-sm">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={item.requires_shirt_sizing}
                              onCheckedChange={(checked) => updateCampaignItem(index, 'requires_shirt_sizing', checked)}
                            />
                            <span>Requires chest/shirt sizing</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={item.requires_pants_sizing}
                              onCheckedChange={(checked) => updateCampaignItem(index, 'requires_pants_sizing', checked)}
                            />
                            <span>Requires waist/pants sizing</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={item.has_sleeve_option}
                              onCheckedChange={(checked) => updateCampaignItem(index, 'has_sleeve_option', checked)}
                            />
                            <span>Has sleeve option</span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditCampaignDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={updateCampaign}
                disabled={savingCampaign}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {savingCampaign ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Order Detail Dialog */}
        <Dialog open={orderDetailDialog} onOpenChange={setOrderDetailDialog}>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Order Details
              </DialogTitle>
            </DialogHeader>

            {selectedOrder && (
              <div className="space-y-4 py-4">
                {/* Parent Info */}
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Submitted by</p>
                  <p className="font-medium">{selectedOrder.parent_name}</p>
                  <p className="text-sm text-slate-500">{selectedOrder.parent_email}</p>
                </div>

                {/* Player/Parent Info */}
                {selectedOrder.is_for_parent ? (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-600 font-medium mb-2">Parent Self-Order</p>
                    {selectedOrder.parent_sizing && (
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <p><span className="text-slate-500">Name:</span> {selectedOrder.parent_sizing.shirt_name}</p>
                        <p><span className="text-slate-500">Number:</span> {selectedOrder.parent_sizing.shirt_number || 'N/A'}</p>
                        <p><span className="text-slate-500">Chest:</span> {selectedOrder.parent_sizing.chest_size}"</p>
                        <p><span className="text-slate-500">Waist:</span> {selectedOrder.parent_sizing.waist_size}"</p>
                        <p><span className="text-slate-500">Shirt Len:</span> {selectedOrder.parent_sizing.shirt_length}"</p>
                        <p><span className="text-slate-500">Trouser Len:</span> {selectedOrder.parent_sizing.trouser_length}"</p>
                        <p><span className="text-slate-500">Sleeves:</span> {selectedOrder.parent_sizing.sleeves}</p>
                      </div>
                    )}
                  </div>
                ) : selectedOrder.player_data && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-600 font-medium mb-2">
                      Player: {selectedOrder.player_data.full_name}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <p><span className="text-slate-500">Shirt Name:</span> {selectedOrder.player_data.shirt_name}</p>
                      <p><span className="text-slate-500">Number:</span> {selectedOrder.player_data.shirt_number || 'N/A'}</p>
                      <p><span className="text-slate-500">Chest:</span> {selectedOrder.player_data.chest_size}"</p>
                      <p><span className="text-slate-500">Waist:</span> {selectedOrder.player_data.waist_size}"</p>
                      <p><span className="text-slate-500">Shirt Len:</span> {selectedOrder.player_data.shirt_length}"</p>
                      <p><span className="text-slate-500">Trouser Len:</span> {selectedOrder.player_data.trouser_length}"</p>
                      <p><span className="text-slate-500">Sleeves:</span> {selectedOrder.player_data.sleeves}</p>
                    </div>
                  </div>
                )}

                {/* Ordered Items */}
                {selectedOrder.items?.length > 0 && (
                  <div>
                    <p className="font-medium mb-2">Ordered Items</p>
                    <div className="space-y-2">
                      {selectedOrder.items.map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <span className="capitalize">{getItemLabel(item.item_type)}</span>
                          <div className="flex items-center gap-3">
                            {item.sleeves && <Badge variant="outline">{item.sleeves}</Badge>}
                            <Badge>x{item.quantity}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedOrder.notes && (
                  <div>
                    <p className="font-medium mb-2">Notes</p>
                    <p className="text-sm text-slate-600 p-2 bg-slate-50 rounded">{selectedOrder.notes}</p>
                  </div>
                )}

                {/* Status */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <Badge className={
                      selectedOrder.status === 'processed' ? 'bg-green-100 text-green-700' :
                      selectedOrder.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }>
                      {selectedOrder.status}
                    </Badge>
                  </div>
                  {selectedOrder.status === 'pending' && (
                    <Button
                      onClick={() => {
                        updateOrderStatus(selectedOrder.id, 'processed');
                        setOrderDetailDialog(false);
                      }}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark as Processed
                    </Button>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setOrderDetailDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Order Dialog */}
        <Dialog open={editOrderDialog} onOpenChange={setEditOrderDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-blue-600" />
                Edit Order
              </DialogTitle>
              <DialogDescription>
                Modify order items, status, and notes.
              </DialogDescription>
            </DialogHeader>

            {editingOrder && (
              <div className="space-y-4 py-4">
                {/* Order Info */}
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium">{editingOrder.parent_name}</p>
                  <p className="text-xs text-slate-500">
                    {editingOrder.is_for_parent ? 'Self Order' : `For: ${editingOrder.player_data?.full_name || 'Player'}`}
                  </p>
                </div>

                {/* Status */}
                <div>
                  <Label>Status</Label>
                  <Select
                    value={editOrderForm.status}
                    onValueChange={(v) => setEditOrderForm(prev => ({ ...prev, status: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="processed">Processed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Items */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Items</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addItemToOrder}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Item
                    </Button>
                  </div>
                  
                  {editOrderForm.items.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No items - verification only</p>
                  ) : (
                    <div className="space-y-2">
                      {editOrderForm.items.map((item, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                          <Select
                            value={item.item_type}
                            onValueChange={(v) => updateOrderItem(index, 'item_type', v)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="full_kit">Full Kit</SelectItem>
                              <SelectItem value="shirt">Shirt</SelectItem>
                              <SelectItem value="pants">Pants</SelectItem>
                              <SelectItem value="cap">Cap</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateOrderItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            className="w-16"
                          />
                          <Select
                            value={item.sleeves || 'short'}
                            onValueChange={(v) => updateOrderItem(index, 'sleeves', v)}
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="short">Short</SelectItem>
                              <SelectItem value="full">Full</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeOrderItem(index)}
                            className="text-red-500"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={editOrderForm.notes}
                    onChange={(e) => setEditOrderForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Order notes..."
                    rows={2}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOrderDialog(false)}>
                Cancel
              </Button>
              <Button onClick={updateOrder} className="bg-blue-600 hover:bg-blue-700">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Order Confirmation Dialog */}
        <Dialog open={deleteOrderDialog} onOpenChange={setDeleteOrderDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Delete Order
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this order? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            {orderToDelete && (
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="font-medium">{orderToDelete.parent_name}</p>
                <p className="text-sm text-slate-600">
                  {orderToDelete.is_for_parent ? 'Self Order' : `For: ${orderToDelete.player_data?.full_name || 'Player'}`}
                </p>
                {orderToDelete.items?.length > 0 && (
                  <p className="text-sm text-slate-500 mt-1">
                    {orderToDelete.items.map(i => `${i.quantity}x ${getItemLabel(i.item_type)}`).join(', ')}
                  </p>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteOrderDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={deleteOrder} 
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Order
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* iter307aj — Admin "Add Order" dialog. */}
        <AdminAddUniformOrderDialog
          open={addOrderDialog}
          onOpenChange={setAddOrderDialog}
          campaign={selectedCampaign}
          onCreated={() => selectedCampaign && fetchCampaignOrders(selectedCampaign.id)}
        />
      </div>
    </DashboardLayout>
  );
};

export default UniformOrder;
